import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Modal, ScrollView, useWindowDimensions, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton, { ButtonTypes } from '../CustomButton';
import { translate } from '../../services/translate';
import { faBarsSort, faX } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import MyAppText from '../MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props {
    children: React.ReactNode;
    onCleanFilters: () => void;
    onFilters: () => void;
    onStatusChange?: (isOpen: boolean) => void;
    filterIsLoading?: boolean;
    applyIsLoading?: boolean;
    applyDisabled?: boolean;
    type?: ButtonTypes;
}

function Filters({ children, onCleanFilters, onFilters, onStatusChange, filterIsLoading, applyIsLoading, type, applyDisabled }: Props) {
    const { styles, breakpoint, theme } = useStyles(stylesheet);
    const insets = useSafeAreaInsets();
    const { width, height } = useWindowDimensions();

    const sidebarWidth = useMemo(() => {
        if (breakpoint === 'xs') {
            return width * 0.8;
        }

        return width * 0.4;
    }, [width, breakpoint]);

    const [isOpen, setIsOpen] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const animationSidebar = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(animationSidebar, {
            toValue: isOpen ? sidebarWidth : 0,
            duration: 200,
            useNativeDriver: true
        }).start();

        onStatusChange?.(isOpen);
    }, [isOpen, sidebarWidth]);

    useEffect(() => {
        if (isOpen) {
            setIsModalVisible(true);
        } else {
            setTimeout(() => setIsModalVisible(false), 200);
        }
    }, [isOpen]);



    return <>
        <CustomButton
            onPress={() => setIsOpen(!isOpen)}
            text={translate('filters')}
            icon={faBarsSort as IconDefinition}
            type={type ?? 'secondary'}
            isLoading={filterIsLoading}
        />
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => setIsOpen(false)}
        >
            <View
                style={{
                    backgroundColor: '#31313199',
                    flex: 1,
                }}
            >
                <Animated.View
                    style={[
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        { position: 'fixed' },
                        styles.container(height, insets),
                        { width: animationSidebar, borderWidth: isOpen ? 1 : 0 },
                    ]}
                >
                    {isOpen &&
                        <>
                            <View style={styles.header}>
                                <View>
                                    <MyAppText style={{ fontSize: 18 }}>{translate('allFilters')}</MyAppText>
                                </View>

                                <FontAwesomeIcon
                                    icon={faX as IconDefinition}
                                    color={theme.colors.iconColor}
                                    fontSize={12}
                                    cursor='pointer'
                                    onClick={() => setIsOpen(!isOpen)}
                                />
                            </View>

                            <View style={[styles.filterContainer]}>
                                <View style={styles.inputsHeight}>
                                    <ScrollView contentContainerStyle={styles.scrollContent}>
                                        <View style={styles.children}>
                                            {children}
                                        </View>
                                        <View style={styles.buttonsContainer}>
                                            <CustomButton
                                                text={translate('filterResults')}
                                                onPress={() => {
                                                    onFilters();
                                                    setIsOpen(!isOpen);
                                                }}
                                                disabled={applyDisabled}
                                                isLoading={applyIsLoading}
                                            />
                                            <CustomButton text={translate('cleanFilters')} onPress={onCleanFilters} type='secondary' />
                                        </View>
                                    </ScrollView>
                                </View>
                            </View>
                        </>
                    }
                </Animated.View>
            </View>
        </Modal>
    </>;
}


const stylesheet = createStyleSheet(theme => ({
    container: (height: number, insets: EdgeInsets) => {
        const top = 80;
        const heightAndTopInset = top + insets.top;

        return {
            backgroundColor: theme.colors.container.background,
            top: heightAndTopInset,
            height: height - heightAndTopInset - insets.bottom - 15,
            right: 0,
            zIndex: 10,
            margin: 0,
            borderRadius: 4,
            borderColor: theme.colors.container.border,
        };
    },

    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 15
    },

    filterContainer: {
        padding: 15,
        flex: 1
    },

    inputsHeight: {
        flex: 1,
    },

    buttonsContainer: {
        flexDirection: 'column',
        gap: 10,
    },

    children: {
        zIndex: 2,
        gap: 10
    },
    scrollContent: {
        flex: 1,
        gap: 20,
        justifyContent: 'space-between'
    }
}));

export default Filters;
